import React, { createContext, useContext, useMemo } from 'react'
import { Theme } from '@mui/material/styles'
import { createTheme as createMuiTheme } from '@mui/material/styles'
import { ClientContext } from './clientContext'
import { ClientTheme, getColorsForTheme } from '../global-styles/clientColors'
import { createBaseThemeOptions } from '../global-styles/nexus-theme/flexible-base-theme-options'
import { createDarkThemeOptions } from '../global-styles/nexus-theme/flexible-dark-theme-options'

interface ThemeContextType {
  theme: Theme
  currentTheme: ClientTheme
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const getThemeForClient = (clientId: string, clients: any[]): ClientTheme => {
  // If no client is selected, use default theme
  if (!clientId) {
    return 'default'
  }
  
  // Find the client to determine theme
  const client = clients?.find(c => c.id === clientId)
  if (!client) {
    return 'default'
  }
  
  // Check if this is Galpin client (adjust this logic based on actual client data structure)
  if (client.name?.toLowerCase().includes('galpin')) {
    return 'galpin'
  }
  
  // Default to autonomy theme for other clients
  return 'autonomy'
}

const createClientTheme = (clientTheme: ClientTheme): Theme => {
  const colors = getColorsForTheme(clientTheme)
  
  // Create theme options with client-specific colors
  const clientBaseThemeOptions = createBaseThemeOptions(colors)
  const clientDarkThemeOptions = createDarkThemeOptions(colors)
  
  // Create the theme using MUI's createTheme
  const theme = createMuiTheme(clientBaseThemeOptions, clientDarkThemeOptions, {
    direction: 'ltr',
  })
  
  return theme
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const clientContext = useContext(ClientContext)
  
  const currentTheme = useMemo(() => {
    return getThemeForClient(clientContext?.clientId || '', clientContext?.clients || [])
  }, [clientContext?.clientId, clientContext?.clients])
  
  const theme = useMemo(() => {
    return createClientTheme(currentTheme)
  }, [currentTheme])
  
  const value = {
    theme,
    currentTheme,
  }
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}